<template>
  <div class="pb-10">
    <PageTitle title="Factures fournisseurs" :description="`${pagination.total || 0} facture(s) au total`">
      <template #actions>
        <BaseButton variant="primary" @click="onAddInvoice">
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Nouvelle facture
        </BaseButton>
      </template>
    </PageTitle>

    <!-- Statistiques -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <BaseCard class="flex items-center gap-4 p-5 hover:shadow-md transition-shadow">
        <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center flex-shrink-0">
          <i class="fa-solid fa-clock text-orange-600 text-xl"></i>
        </div>
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-600">En attente</p>
          <p class="text-2xl font-bold text-gray-900">{{ stats.pending }}</p>
        </div>
      </BaseCard>

      <BaseCard class="flex items-center gap-4 p-5 hover:shadow-md transition-shadow">
        <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-100 to-yellow-200 flex items-center justify-center flex-shrink-0">
          <i class="fa-solid fa-circle-half-stroke text-yellow-600 text-xl"></i>
        </div>
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-600">Partiellement payées</p>
          <p class="text-2xl font-bold text-gray-900">{{ stats.partial }}</p>
        </div>
      </BaseCard>

      <BaseCard class="flex items-center gap-4 p-5 hover:shadow-md transition-shadow">
        <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center flex-shrink-0">
          <i class="fa-solid fa-check text-green-600 text-xl"></i>
        </div>
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-600">Payées</p>
          <p class="text-2xl font-bold text-gray-900">{{ stats.paid }}</p>
        </div>
      </BaseCard>

      <BaseCard class="flex items-center gap-4 p-5 hover:shadow-md transition-shadow">
        <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0">
          <i class="fa-solid fa-file-invoice text-blue-600 text-xl"></i>
        </div>
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-600">Total</p>
          <p class="text-2xl font-bold text-gray-900">{{ pagination.total }}</p>
        </div>
      </BaseCard>
    </div>

    <!-- Filtres -->
    <BaseCard class="mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput
            v-model="filters.search"
            type="search"
            placeholder="Rechercher par référence, fournisseur..."
            clearable
            @input="debouncedFetch"
          >
            <template #icon-left>
              <i class="fa-solid fa-search text-gray-400"></i>
            </template>
          </BaseInput>
          </div>

        <!-- Filtre statut -->
        <div class="min-w-[200px]">
          <select
            v-model="filters.status"
            @change="onStatusFilterChange"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="partial">Partiellement payée</option>
            <option value="paid">Payée</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>

        <!-- Bouton actualiser -->
        <BaseButton variant="ghost" size="sm" @click="fetchInvoices" title="Actualiser">
          <template #icon-left>
            <i class="fa-solid fa-rotate"></i>
          </template>
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Tableau -->
    <BaseCard padding="none" class="overflow-hidden">
      <!-- État de chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement des factures...</p>
      </div>

      <!-- État vide -->
      <div v-else-if="invoices.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i class="fa-solid fa-file-invoice text-gray-400 text-3xl"></i>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">Aucune facture trouvée</h3>
        <p class="text-gray-600 mb-4">
          <span v-if="filters.search || filters.status">Essayez de modifier vos critères de recherche.</span>
          <span v-else>Commencez par créer votre première facture fournisseur.</span>
        </p>
        <BaseButton v-if="!filters.search && !filters.status" variant="primary" @click="onAddInvoice">
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Créer la première facture
        </BaseButton>
        </div>

      <!-- Tableau des factures -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[180px]">
                      Référence
                  </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                      Fournisseur
                  </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                Date
                  </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">
                      Montant
                  </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                      Statut
              </th>
              <th class="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-[60px]">
                Actions
                  </th>
                </tr>
              </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="invoice in invoices"
              :key="invoice.id"
              class="hover:bg-gray-50 transition-colors cursor-pointer"
              @click="goToDetails(invoice.id)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-file-invoice text-blue-600"></i>
                      </div>
                  <span class="text-sm font-medium text-gray-900">{{ invoice.invoice_number }}</span>
                    </div>
                  </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {{ getInitials(invoice.supplier?.name) }}
                      </div>
                  <span class="text-sm font-medium text-gray-900">{{ invoice.supplier?.name || '-' }}</span>
                    </div>
                  </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <i class="fa-solid fa-calendar text-gray-400"></i>
                      {{ formatDate(invoice.invoice_date) }}
                    </div>
                  </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex flex-col gap-1">
                  <div class="text-sm font-semibold text-gray-900">{{ formatCurrency(invoice.total_amount) }}</div>
                  <div v-if="invoice.paid_amount > 0" class="text-xs text-green-600 font-medium">
                        Payé: {{ formatCurrency(invoice.paid_amount) }}
                      </div>
                    </div>
                  </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-semibold"
                  :class="getStatusClasses(invoice.status)"
                >
                  <span class="w-2 h-2 rounded-full" :class="getStatusDotClasses(invoice.status)"></span>
                      {{ getStatusLabel(invoice.status) }}
                    </span>
                  </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <BaseButton
                  variant="icon"
                  size="sm"
                  @click.stop="goToDetails(invoice.id)"
                  title="Voir les détails"
                >
                  <i class="fa-solid fa-chevron-right"></i>
                </BaseButton>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

      <!-- Pagination -->
      <PaginationComponent
        v-if="invoices.length > 0"
        :current-page="pagination.page"
        :per-page="pagination.per_page"
              :total="pagination.total"
        :to="pagination.to"
        :from="pagination.from"
        @page-change="goToPage"
      />
    </BaseCard>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import PageTitle from '@/components/common/PageTitle.vue'
import supplierInvoiceService from '@/services/supplierInvoiceService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useRouter } from 'vue-router'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'

export default defineComponent({
  name: 'SupplierInvoicesView',
  components: {
    PageTitle,
    PaginationComponent,
    BaseCard,
    BaseButton,
    BaseInput
  },
  setup() {
    const loading = ref(false)
    const router = useRouter()

    const invoices = ref([])
    const filters = ref({
      search: '',
      status: ''
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })
    let debounceTimer = null

    const stats = computed(() => {
      if (!Array.isArray(invoices.value)) return { pending: 0, partial: 0, paid: 0 }
      const pending = invoices.value.filter(i => i.status === 'pending').length
      const partial = invoices.value.filter(i => i.status === 'partial').length
      const paid = invoices.value.filter(i => i.status === 'paid').length
      return { pending, partial, paid }
    })

    const fetchInvoices = async () => {
      try {
        loading.value = true
        const res = await supplierInvoiceService.getSupplierInvoices({
          search: filters.value.search || undefined,
          status: filters.value.status || undefined,
          per_page: pagination.value.per_page,
          page: pagination.value.page
        })
        const data = res.data
        // Gérer différents formats de réponse de l'API
        if (data) {
          if (Array.isArray(data)) {
            invoices.value = data
          } else if (data.data && Array.isArray(data.data)) {
            // Format paginé Laravel
            invoices.value = data.data
            pagination.value.total = Number(data.total || 0)
            pagination.value.per_page = Number(data.per_page || pagination.value.per_page)
            pagination.value.page = Number(data.current_page || pagination.value.page)
            pagination.value.from = Number(data.from ?? ((pagination.value.page - 1) * pagination.value.per_page + 1))
            pagination.value.to = Number(data.to ?? Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total))
          } else if (data.items && Array.isArray(data.items)) {
            // Format alternatif avec items
            invoices.value = data.items
          } else if (data.meta) {
            // Format avec meta
            invoices.value = data.data || []
            pagination.value.total = Number(data.meta.total || 0)
            pagination.value.per_page = Number(data.meta.per_page || pagination.value.per_page)
            pagination.value.page = Number(data.meta.current_page || pagination.value.page)
            pagination.value.from = Number(data.meta.from ?? data.from ?? ((pagination.value.page - 1) * pagination.value.per_page + 1))
            pagination.value.to = Number(data.meta.to ?? data.to ?? Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total))
          } else {
            invoices.value = []
          }
        } else {
          invoices.value = []
        }

        // S'assurer que les valeurs de pagination sont correctes
        if (!pagination.value.total) {
          pagination.value.total = Array.isArray(invoices.value) ? invoices.value.length : 0
        }
        if (!pagination.value.per_page) {
          pagination.value.per_page = 10
        }
        if (!pagination.value.page) {
          pagination.value.page = 1
        }
        if (!pagination.value.from && pagination.value.total > 0) {
          pagination.value.from = (pagination.value.page - 1) * pagination.value.per_page + 1
        }
        if (!pagination.value.to && pagination.value.total > 0) {
          pagination.value.to = Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)
        }
      } catch (e) {
        console.error('Erreur chargement factures:', e)
        invoices.value = []
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        pagination.value.page = 1
        fetchInvoices()
      }, 300)
    }

    const clearSearch = () => {
      filters.value.search = ''
      pagination.value.page = 1
      fetchInvoices()
    }

    const onStatusFilterChange = () => {
      pagination.value.page = 1
      fetchInvoices()
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchInvoices()
    }

    const onAddInvoice = () => {
      router.push({ name: 'supplier-invoices-new' })
    }

    const goToDetails = (id) => {
      router.push({ name: 'supplier-invoice-details', params: { id } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      return supplierInvoiceService.formatDate(dateStr)
    }

    const formatCurrency = (amount) => {
      return supplierInvoiceService.formatCurrency(amount)
    }

    const getStatusLabel = (status) => {
      return supplierInvoiceService.getStatusLabel(status)
    }

    const getStatusClasses = (status) => {
      const map = {
        pending: 'bg-orange-50 text-orange-700',
        paid: 'bg-green-50 text-green-700',
        partial: 'bg-yellow-50 text-yellow-700',
        cancelled: 'bg-red-50 text-red-700',
        overdue: 'bg-pink-50 text-pink-700'
      }
      return map[status] || 'bg-gray-50 text-gray-700'
    }

    const getStatusDotClasses = (status) => {
      const map = {
        pending: 'bg-orange-500',
        paid: 'bg-green-500',
        partial: 'bg-yellow-500',
        cancelled: 'bg-red-500',
        overdue: 'bg-pink-500'
      }
      return map[status] || 'bg-gray-500'
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    fetchInvoices()

    return {
      loading,
      invoices,
      filters,
      pagination,
      stats,
      debouncedFetch,
      clearSearch,
      onStatusFilterChange,
      goToPage,
      onAddInvoice,
      formatDate,
      formatCurrency,
      goToDetails,
      getStatusLabel,
      getStatusClasses,
      getStatusDotClasses,
      getInitials,
      fetchInvoices
    }
  }
})
</script>

<style scoped>
/* Minimal scoped styles - Tailwind handles most styling */
</style>
